import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight, ArrowLeft, Box } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Eventos = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEventos = async () => {
      setLoading(true);
      try {
        const response = await apiService.getEventos();
        if (response.estado === 1) {
          setEventos(response.data);
        } else {
          setEventos([]);
        }
      } catch (error) {
        console.error('Error cargando eventos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEventos();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center pt-20">
        <motion.div 
          className="w-16 h-16 border-4 border-yellow-600/30 border-t-yellow-500 rounded-full" 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }} 
        />
        <p className="text-yellow-500/70 mt-4 text-sm tracking-widest uppercase">Cargando eventos...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* --- ENCABEZADO --- */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-yellow-500 transition-colors mb-8 group text-sm font-medium tracking-wide uppercase"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Volver</span>
          </button>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Nuestros <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">Eventos</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent mx-auto mb-6 rounded-full" />
          <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
            Revive los momentos más increíbles que hemos producido con excelencia y tecnología de punta.
          </p>
        </motion.div>

        {/* --- GRID DE EVENTOS --- */}
        {eventos.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="text-center py-24 bg-neutral-900/30 rounded-3xl border border-yellow-600/20 max-w-2xl mx-auto"
          >
            <Box className="w-16 h-16 text-yellow-600/40 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">No hay eventos publicados</h3>
            <p className="text-gray-400 mb-6">Próximamente agregaremos nuevos eventos a nuestra galería.</p>
          </motion.div>
        ) : (
          <div className="flex flex-wrap justify-center gap-6 lg:gap-8">
            {eventos.map((evento, index) => (
              <motion.div
                key={evento.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="w-full sm:w-1/2 lg:w-1/3 xl:w-1/4"
              >
                <Link to={`/evento/${evento.slug}`} className="group block h-full">
                  <div className="relative h-full bg-neutral-900/40 border border-yellow-600/20 rounded-2xl overflow-hidden hover:border-yellow-500/50 transition-all duration-500 hover:shadow-[0_10px_40px_-15px_rgba(234,179,8,0.2)] flex flex-col">
                    
                    {/* Imagen de Portada */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-black">
                      <img 
                        src={evento.imagen_portada_url} 
                        alt={evento.titulo} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                      
                      {/* Badge Destacado */}
                      {evento.destacado === 1 && (
                        <div className="absolute top-3 right-3">
                          <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                            Destacado
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Contenido */}
                    <div className="p-5 flex flex-col flex-grow">
                      <h3 className="text-lg font-bold text-white mb-3 group-hover:text-yellow-500 transition-colors duration-300 line-clamp-2 min-h-[3.5rem]">
                        {evento.titulo}
                      </h3>
                      
                      <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3 flex-grow">
                        {evento.resumen}
                      </p>

                      {/* Meta info */}
                      <div className="space-y-2 mb-4 text-sm text-gray-500">
                        {evento.fecha_evento && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-yellow-600" />
                            <span>{formatDate(evento.fecha_evento)}</span>
                          </div>
                        )}
                        {evento.ubicacion && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-yellow-600" />
                            <span className="line-clamp-1">{evento.ubicacion}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Botón */}
                      <div className="mt-auto pt-4 border-t border-yellow-600/20 flex items-center text-yellow-500 font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                        <span>Ver detalles</span>
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Eventos;